import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils.js';

// ─── Types ────────────────────────────────────────────────────

export interface CollapsibleSectionProps {
  /** Header content rendered to the left of the chevron toggle. */
  trigger: React.ReactNode;
  /** Body content shown when the section is expanded. */
  children: React.ReactNode;
  /** Controls open state externally. When provided, component is fully controlled. */
  open?: boolean;
  /** Called when the header toggle is clicked in controlled mode. */
  onToggle?: () => void;
  /** Initial open state for uncontrolled mode. @default false */
  defaultOpen?: boolean;
  /**
   * When provided, an expand-all / collapse-all toolbar row is rendered
   * at the top of the body (inside the border-t separator).
   */
  expandAllLabel?: string;
  collapseAllLabel?: string;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  allExpanded?: boolean;
  className?: string;
  /** data-testid applied to the root wrapper. */
  testId?: string;
  /** data-testid applied to the header toggle button. */
  toggleTestId?: string;
  /** data-testid applied to the expand-all button. */
  expandAllTestId?: string;
  /**
   * Optional slot rendered to the right of the toggle button in the header row.
   * Useful for action controls (e.g. navigation buttons, selects) that should
   * remain visible alongside the toggle but should NOT be part of the clickable
   * toggle area.
   */
  headerActions?: React.ReactNode;
}

// ─── Internal helpers ─────────────────────────────────────────

interface ExpandAllBarProps {
  allExpanded: boolean | undefined;
  expandAllLabel: string;
  collapseAllLabel: string;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  testId: string | undefined;
}

function ExpandAllBar({
  allExpanded,
  expandAllLabel,
  collapseAllLabel,
  onExpandAll,
  onCollapseAll,
  testId,
}: ExpandAllBarProps) {
  return (
    <div className="flex justify-end px-4 py-1.5 border-b border-border/50 bg-muted/10">
      <button
        type="button"
        className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
        onClick={allExpanded ? onCollapseAll : onExpandAll}
        data-testid={testId}
      >
        {allExpanded ? collapseAllLabel : expandAllLabel}
      </button>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────

export function CollapsibleSection({
  trigger,
  children,
  open: controlledOpen,
  onToggle,
  defaultOpen = false,
  expandAllLabel,
  collapseAllLabel,
  onExpandAll,
  onCollapseAll,
  allExpanded,
  className,
  testId,
  toggleTestId,
  expandAllTestId,
  headerActions,
}: CollapsibleSectionProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  function handleToggle() {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalOpen((v) => !v);
    }
  }

  const showExpandAll =
    expandAllLabel !== undefined &&
    collapseAllLabel !== undefined &&
    onExpandAll !== undefined &&
    onCollapseAll !== undefined;

  return (
    <div
      className={cn('rounded-md border border-border shadow-sm overflow-hidden', className)}
      data-testid={testId}
    >
      {/*
       * flex-wrap (CU-86cb8grgk): on narrow viewports, `headerActions` (e.g.
       * `DetailAvailabilityCard`'s prev/next window steppers + granularity select)
       * combined with the title/toggle can exceed the header's width. Without wrap,
       * the overflowing controls get clipped by this section's own `overflow-hidden`
       * root wrapper below instead of just moving to a second line.
       */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <button
          type="button"
          className="flex items-center gap-2.5 grow hover:opacity-75 transition-opacity text-left"
          onClick={handleToggle}
          data-testid={toggleTestId}
          aria-expanded={isOpen}
        >
          <div className="flex items-center gap-2.5 grow">{trigger}</div>
          <span className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-background shrink-0">
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180',
              )}
            />
          </span>
        </button>
        {headerActions != null ? (
          <div className="flex items-center gap-1 shrink-0">{headerActions}</div>
        ) : null}
      </div>

      {isOpen ? (
        <div className="border-t border-border">
          {showExpandAll ? (
            <ExpandAllBar
              allExpanded={allExpanded}
              expandAllLabel={expandAllLabel}
              collapseAllLabel={collapseAllLabel}
              onExpandAll={onExpandAll}
              onCollapseAll={onCollapseAll}
              testId={expandAllTestId}
            />
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
}
